'use client';

import * as React from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';

import { cn } from '@/lib/utils';

type CustomProgressProps = React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> & {
  indicatorClassName?: string;
};

const Progress = React.forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  CustomProgressProps
>(({ className, indicatorClassName, value, ...props }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    className={cn('relative h-4 w-full overflow-hidden rounded-full bg-secondary', className)}
    {...props}
  >
    <ProgressPrimitive.Indicator
      className={cn('h-full w-full flex-1 bg-primary transition-all', indicatorClassName)}
      style={{
        transition: 'transform 0.3s ease-in-out',
        transform: `translateX(-${100 - (value || 0)}%)`,
      }}
    />
  </ProgressPrimitive.Root>
));
Progress.displayName = ProgressPrimitive.Root.displayName;

const LoadingBar = ({
  className,
  indicatorClassName,
  durationInSeconds = 10,
}: {
  className?: string;
  indicatorClassName?: string;
  durationInSeconds?: number;
}) => {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setProgress(oldProgress => {
        if (oldProgress === 100) {
          return 0;
        }
        return oldProgress + 1;
      });
    }, durationInSeconds * 10);

    return () => clearInterval(interval);
  }, []);

  return (
    <Progress
      className={cn('h-4 w-full overflow-hidden rounded-full bg-secondary', className)}
      indicatorClassName={cn('bg-gradient-to-r from-blue-500 to-blue-700', indicatorClassName)}
      value={progress}
    />
  );
};

export { Progress, LoadingBar };
